import * as React from 'react'
import type { Session } from '@supabase/supabase-js'
import { sb } from '@/lib/supabase'
import type { MemberRole, Profile, Team } from '@/lib/types'

const TEAM_STORAGE_KEY = 'teamaccel-current-team'

export interface Membership {
  team: Team
  role: MemberRole
  memberId: string
}

interface AuthState {
  session: Session | null
  profile: Profile | null
  memberships: Membership[]
  currentTeam: Team | null
  currentRole: MemberRole | null
  /** DPL staff: full access to every team plus the Admin section. */
  isAdmin: boolean
  /** Can restructure this team (define metrics, manage roster). Lead or coach. */
  isLead: boolean
  /**
   * DPL coach on this team, or DPL staff. Narrower than `isLead` — it excludes
   * the team's own lead, which matters for the monthly report: the coach writes
   * it, the lead reads it (migrations/0018_reports.sql).
   */
  isCoach: boolean
  loading: boolean
  error: string | null
  setCurrentTeamId: (id: string) => void
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = React.createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/**
 * Convenience for the many pages that only make sense with a team selected.
 * Everything under <RequireTeam> is guaranteed one, so this narrows the type
 * instead of making every page null-check.
 */
export function useTeam(): { team: Team; role: MemberRole | null; isLead: boolean; isAdmin: boolean } {
  const { currentTeam, currentRole, isLead, isAdmin } = useAuth()
  if (!currentTeam) throw new Error('useTeam used outside a team-scoped route')
  return { team: currentTeam, role: currentRole, isLead, isAdmin }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<Session | null>(null)
  const [profile, setProfile] = React.useState<Profile | null>(null)
  const [memberships, setMemberships] = React.useState<Membership[]>([])
  const [currentTeamId, setCurrentTeamIdState] = React.useState<string | null>(
    () => localStorage.getItem(TEAM_STORAGE_KEY),
  )
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  // Subscribe to auth state once. onAuthStateChange fires immediately with the
  // persisted session, so there's no separate getSession() call to race with.
  React.useEffect(() => {
    let active = true
    const { data } = sb().auth.onAuthStateChange((_event, next) => {
      if (!active) return
      setSession(next)
      if (!next) {
        setProfile(null)
        setMemberships([])
        setLoading(false)
      }
    })
    // Cover the case where no event fires (no stored session at all).
    void sb()
      .auth.getSession()
      .then(({ data: { session: existing } }) => {
        if (!active) return
        setSession(existing)
        if (!existing) setLoading(false)
      })
    return () => {
      active = false
      data.subscription.unsubscribe()
    }
  }, [])

  const load = React.useCallback(async (userId: string) => {
    setError(null)
    try {
      // The profile row is created by a trigger on auth.users, so on a brand
      // new account it may not be visible on the very first read. Retry a
      // couple of times before deciding something is wrong.
      let prof: Profile | null = null
      for (let attempt = 0; attempt < 3 && !prof; attempt++) {
        const { data, error: perr } = await sb()
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle()
        if (perr) throw perr
        prof = data as Profile | null
        if (!prof) await new Promise((r) => setTimeout(r, 300))
      }
      setProfile(prof)

      // One round trip for memberships + their teams. DPL admins additionally
      // need every team, since they aren't members of the ones they coach.
      const { data: rows, error: merr } = await sb()
        .from('team_members')
        .select('id, role, team:teams!inner(*)')
        .eq('user_id', userId)
        .eq('active', true)
      if (merr) throw merr

      const mine: Membership[] = (rows ?? []).flatMap((r: any) =>
        r.team ? [{ team: r.team as Team, role: r.role as MemberRole, memberId: r.id }] : [],
      )

      let all = mine
      if (prof?.is_dpl_admin) {
        const { data: teams, error: terr } = await sb()
          .from('teams')
          .select('*')
          .order('name')
        if (terr) throw terr
        const byId = new Map(mine.map((m) => [m.team.id, m]))
        all = (teams ?? []).map((t: Team) => byId.get(t.id) ?? { team: t, role: 'dpl_coach', memberId: '' })
      }

      all.sort((a, b) => a.team.name.localeCompare(b.team.name))
      setMemberships(all)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (session?.user?.id) {
      setLoading(true)
      void load(session.user.id)
    }
  }, [session?.user?.id, load])

  const setCurrentTeamId = React.useCallback((id: string) => {
    localStorage.setItem(TEAM_STORAGE_KEY, id)
    setCurrentTeamIdState(id)
  }, [])

  // Fall back to the first team when nothing is stored, or when the stored id
  // is stale (membership revoked, team deleted, or a different account).
  const resolved = React.useMemo(() => {
    if (memberships.length === 0) return null
    return memberships.find((m) => m.team.id === currentTeamId) ?? memberships[0]
  }, [memberships, currentTeamId])

  React.useEffect(() => {
    if (resolved && resolved.team.id !== currentTeamId) {
      localStorage.setItem(TEAM_STORAGE_KEY, resolved.team.id)
      setCurrentTeamIdState(resolved.team.id)
    }
  }, [resolved, currentTeamId])

  const signOut = React.useCallback(async () => {
    await sb().auth.signOut()
    localStorage.removeItem(TEAM_STORAGE_KEY)
  }, [])

  const refresh = React.useCallback(async () => {
    if (session?.user?.id) await load(session.user.id)
  }, [session?.user?.id, load])

  const isAdmin = profile?.is_dpl_admin ?? false
  const value: AuthState = {
    session,
    profile,
    memberships,
    currentTeam: resolved?.team ?? null,
    currentRole: resolved?.role ?? null,
    isAdmin,
    isLead: isAdmin || resolved?.role === 'team_lead' || resolved?.role === 'dpl_coach',
    isCoach: isAdmin || resolved?.role === 'dpl_coach',
    loading,
    error,
    setCurrentTeamId,
    signOut,
    refresh,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
